import { useState, useEffect } from "react";
import { useUsage } from "./useUsage";
import type { Plan } from "../types/payments";

export type PlanTier = Plan;

export interface SubscriptionFeatures {
  // Email frequency permissions
  canUseWeeklyEmails: boolean;
  canUseMonthlyEmails: boolean;
  // Feature access
  canUseAutomations: boolean;
  canUseDataRoom: boolean;
  canUseDecks: boolean;
  canUseCustomApiKeys: boolean;
  // Limits
  maxInsights: number; // -1 for unlimited
  maxEmails: number; // -1 for unlimited
  sendsPerWeek: number; // -1 for unlimited
  unlimitedSends: boolean;
  // Plan info
  planTier: PlanTier;
  planName: string;
  // Weekly send tracking (for Free tier)
  weeklySendsUsed?: number;
  weeklySendsRemaining?: number;
  canSendThisWeek?: boolean;
}

/**
 * Hook to manage subscription-based feature access
 * Returns subscription tier and feature availability
 */
export function useSubscription() {
  const { usage, loading, error, refetch } = useUsage();
  const [features, setFeatures] = useState<SubscriptionFeatures>({
    canUseWeeklyEmails: false,
    canUseMonthlyEmails: false,
    canUseAutomations: false,
    canUseDataRoom: false,
    canUseDecks: false,
    canUseCustomApiKeys: false,
    maxInsights: 25,
    maxEmails: 4,
    sendsPerWeek: 1,
    unlimitedSends: false,
    planTier: "free",
    planName: "Free",
  });

  useEffect(() => {
    if (!usage) return;

    // Determine plan tier from usage data
    const planTier = (usage.insights.planTier || "free") as PlanTier;

    // Feature matrix based on plan
    const featureMatrix: Record<PlanTier, SubscriptionFeatures> = {
      free: {
        canUseWeeklyEmails: false,
        canUseMonthlyEmails: false,
        canUseAutomations: false,
        canUseDataRoom: false,
        canUseDecks: false,
        canUseCustomApiKeys: false,
        maxInsights: 25,
        maxEmails: 4, // 1 per week = 4 per month
        sendsPerWeek: 1,
        unlimitedSends: false,
        planTier: "free",
        planName: "Free",
      },
      basic: {
        canUseWeeklyEmails: true,
        canUseMonthlyEmails: true,
        canUseAutomations: true,
        canUseDataRoom: false,
        canUseDecks: false,
        canUseCustomApiKeys: false,
        maxInsights: 100,
        maxEmails: -1, // unlimited
        sendsPerWeek: -1, // unlimited
        unlimitedSends: true,
        planTier: "basic",
        planName: "Basic",
      },
      pro: {
        canUseWeeklyEmails: true,
        canUseMonthlyEmails: true,
        canUseAutomations: true,
        canUseDataRoom: true,
        canUseDecks: true,
        canUseCustomApiKeys: true,
        maxInsights: -1, // unlimited
        maxEmails: -1, // unlimited
        sendsPerWeek: -1, // unlimited
        unlimitedSends: true,
        planTier: "pro",
        planName: "Pro",
      },
      enterprise: {
        canUseWeeklyEmails: true,
        canUseMonthlyEmails: true,
        canUseAutomations: true,
        canUseDataRoom: true,
        canUseDecks: true,
        canUseCustomApiKeys: true,
        maxInsights: -1, // unlimited
        maxEmails: -1, // unlimited
        sendsPerWeek: -1, // unlimited
        unlimitedSends: true,
        planTier: "enterprise",
        planName: "Enterprise",
      },
    };

    const baseFeatures = featureMatrix[planTier] || featureMatrix.free;

    // Add weekly send tracking from usage data if available
    const weeklySends = (usage as any).weeklySends;
    if (weeklySends) {
      baseFeatures.weeklySendsUsed = weeklySends.used;
      baseFeatures.weeklySendsRemaining = weeklySends.remaining;
      baseFeatures.canSendThisWeek = weeklySends.canSend;
    }

    // Override with actual feature access from API if available
    const featuresFromApi = (usage as any).features;
    if (featuresFromApi) {
      baseFeatures.canUseAutomations = featuresFromApi.canUseAutomations;
      baseFeatures.canUseDataRoom = featuresFromApi.canUseDataRoom;
      baseFeatures.canUseDecks = featuresFromApi.canUseDecks;
      if (featuresFromApi.canUseCustomApiKeys !== undefined) {
        baseFeatures.canUseCustomApiKeys = featuresFromApi.canUseCustomApiKeys;
      }
    }

    setFeatures(baseFeatures);
  }, [usage]);

  return {
    features,
    loading,
    error,
    refetch,
    // Plan tier checks
    isFree: features.planTier === "free",
    isBasic: features.planTier === "basic",
    isPro: features.planTier === "pro",
    isEnterprise: features.planTier === "enterprise",
    // Feature access checks
    canUseAutomations: features.canUseAutomations,
    canUseDataRoom: features.canUseDataRoom,
    canUseDecks: features.canUseDecks,
    canUseCustomApiKeys: features.canUseCustomApiKeys,
    // Combined checks
    hasUnlimitedSends: features.unlimitedSends,
    hasPaidPlan: features.planTier !== "free",
  };
}

